import AddIcon from "@mui/icons-material/Add";
import ClearIcon from "@mui/icons-material/Clear";
import SearchIcon from "@mui/icons-material/Search";
import {
  Box,
  Button,
  Container,
  Dialog,
  Fab,
  Grid,
  IconButton,
  InputAdornment,
  TextField,
  Tooltip,
} from "@mui/material";
import Alert from "@mui/material/Alert";
import AutocompleteWithQuery from "components/AutocompleteWithQuery";
import IconQuesionType from "components/IconQuestionType";
import LazyTable, {
  DEFAULT_ROWS_PER_PAGE,
  TableCellType,
  TableSortType,
} from "components/LazyTable";
import OpenLinkInNewTabContextMenuItem from "components/OpenInNewTabContextMenuItem";
import SnackbarMessage from "components/SnackbarMessage";
import TruncatedQuestion from "components/TruncatedQuestion";
import useGlobalContext from "hooks/globalData";
import useBoolean from "hooks/useBoolean";
import useDebounce from "hooks/useDebounce";
import useListTitle from "hooks/useListTitle";
import { useEffect, useState } from "react";
import { FormattedMessage as T, useIntl } from "react-intl";
import { useQuery, useQueryClient } from "react-query";
import { useNavigate } from "react-router-dom";
import {
  AdminListRequestOfQuestionFilter,
  AdminListRequestOfQuestionTypeFilter,
  IQuestion,
  ListResponseOfQuestionType,
  Question,
  QuestionFilter,
} from "services/KnowfullyAdminClient";
import { Organizations } from "@knowfully/ui.common";
import contextStore from "stores/contextStore";
import persistanceStore from "stores/persistanceStore";
import { SnackbarMessageProps } from "types/common-types";
import QuestionCreateForm from "./QuestionCreateForm";

interface QuestionWithId extends Question {
  id: string;
}

const QuestionList = () => {
  const { formatMessage: t } = useIntl();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const {
    questionService,
    questionDetailsUrl,
    questionRatingsManagementUrl,
  } = useGlobalContext();
  const [createOpen, createOpenActions] = useBoolean(false);
  const [
    snackbarMessage,
    setSnackbarMessage,
  ] = useState<SnackbarMessageProps | null>(null);

  const { organizationId } = contextStore.useState();
  const [order, setOrder] = useState<TableSortType>("desc");
  const [orderBy, setOrderBy] = useState("createdOn");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
  const searchTerm = persistanceStore.useState(
    (s) => s.questionFilterSearchTerm
  );
  const trimmedSearchTerm = searchTerm.trim();
  const debouncedSearchTerm = useDebounce(trimmedSearchTerm, 500);
  const questionTypeId = persistanceStore.useState(
    (s) => s.questionFilterTypeId
  );
  const isRootOrganization =
    organizationId === Organizations.KnowfullyLearningGroup;
  const createDisabled = !organizationId || isRootOrganization;

  useListTitle("menubarQuestions");

  const { isError, data, error, isFetching } = useQuery(
    [
      "questions",
      organizationId,
      debouncedSearchTerm,
      page,
      rowsPerPage,
      orderBy,
      order,
      questionTypeId,
    ],
    () =>
      questionService.getQuestions(
        AdminListRequestOfQuestionFilter.fromJS({
          organizationId,
          pageNumber: page,
          pageSize: rowsPerPage,
          sortField: orderBy,
          sortDescending: order === "desc",
          parameters: QuestionFilter.fromJS({
            searchText: debouncedSearchTerm,
            questionTypeId,
          }),
        })
      ),
    {
      enabled: !!organizationId,
      keepPreviousData: true,
    }
  );

  const fetchedQuestionTypes = useQuery<ListResponseOfQuestionType>(
    "questionTypes",
    () =>
      questionService.getQuestionTypes(
        new AdminListRequestOfQuestionTypeFilter({ sortField: "name" })
      )
  );

  useEffect(() => {
    setPage(0);
  }, [debouncedSearchTerm, questionTypeId]);

  useEffect(() => {
    if (createDisabled) {
      createOpenActions.setFalse();
    }
  }, [createDisabled, createOpenActions]);

  if (isError) {
    return (
      <Alert severity="error">
        <pre>{JSON.stringify(error, null, 2)}</pre>
      </Alert>
    );
  }

  const onQuestionCreated = (_: IQuestion, createAnother?: boolean) => {
    queryClient.invalidateQueries("questions");

    if (!createAnother) {
      createOpenActions.setFalse();
    }

    setSnackbarMessage(new SnackbarMessageProps(t({ id: "questionCreated" })));
  };

  const onManageRatings = () => {
    navigate(questionRatingsManagementUrl());
  };

  const questions = data?.data as QuestionWithId[];

  const cells: Array<TableCellType> = [
    {
      accessor: "createdOn",
      align: "left",
      width: 0,
      type: "date",
      label: t({ id: "questionsCreatedOnLabel" }),
    },
    {
      accessor: "value",
      align: "left",
      width: 0,
      type: "node",
      View: TruncatedQuestion,
      label: t({ id: "questionLabel" }),
      noSort: true,
    },
    {
      accessor: "questionType.name",
      align: "center",
      width: 0,
      type: "node",
      View: IconQuesionType,
      label: t({ id: "questionsTypeLabel" }),
      noSort: true,
    },
    {
      accessor: "required",
      align: "center",
      width: 0,
      type: "boolean",
      label: t({ id: "questionsRequiredLabel" }),
      noSort: true,
    },
  ];

  const onClick = (id: string) => {
    navigate(questionDetailsUrl(id));
  };

  return (
    <Container>
      <Box mb={3} textAlign="center">
        <Tooltip
          title={
            isRootOrganization
              ? t({ id: "rootOrganizationIsNotAllowedTooltip" })
              : ""
          }
        >
          <span>
            <Fab
              aria-label="add"
              color="primary"
              onClick={createOpenActions.setTrue}
              disabled={createDisabled}
            >
              <AddIcon />
            </Fab>
          </span>
        </Tooltip>
      </Box>

      <Box mb={3}>
        <Grid container spacing={2}>
          <Grid item sm={12} md={8}>
            <TextField
              fullWidth
              variant="outlined"
              value={searchTerm ?? ""}
              placeholder={t({ id: "searchPlaceholder" })}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() =>
                        persistanceStore.update((s) => {
                          s.questionFilterSearchTerm = "";
                        })
                      }
                    >
                      <ClearIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              onChange={(e) =>
                persistanceStore.update((s) => {
                  s.questionFilterSearchTerm = e.target.value;
                })
              }
            />
          </Grid>
          <Grid item sm={12} md={4}>
            <AutocompleteWithQuery
              value={questionTypeId}
              onChange={(id) =>
                persistanceStore.update((s) => {
                  s.questionFilterTypeId = id;
                })
              }
              labelField="name"
              fetchedData={fetchedQuestionTypes}
              failMessage={t({
                id: "selectQuestionTypeFetchFailed",
              })}
              textFieldProps={{
                variant: "outlined",
              }}
              label={t({ id: "questionsTypeLabel" })}
            />
          </Grid>
        </Grid>
      </Box>

      <Box textAlign="right" mb={1}>
        <Button
          size="small"
          variant="outlined"
          onClick={onManageRatings}
        >
          <T id="questionsManageRatingLink" />
        </Button>
      </Box>

      <LazyTable
        {...{
          rowCount: data?.totalRecords ?? 0,
          rows: questions ?? [],
          loading: isFetching,

          dense: true,
          title: t({ id: "menubarQuestions" }),
          cells,
          page,
          setPage,
          order,
          setOrder,
          orderBy,
          setOrderBy,
          rowsPerPage,
          setRowsPerPage,
          onClick,
          contextMenuItems: [
            OpenLinkInNewTabContextMenuItem((id) => questionDetailsUrl(id)),
          ],
        }}
      />

      <Dialog open={createOpen} disableEnforceFocus maxWidth="sm">
        <QuestionCreateForm
          organizationId={organizationId!}
          onQuestionCreated={onQuestionCreated}
          onCancel={createOpenActions.setFalse}
        />
      </Dialog>
      <SnackbarMessage
        snackbarMessage={snackbarMessage}
        setSnackbarMessage={setSnackbarMessage}
      />
    </Container>
  );
};

export default QuestionList;
